import type {
  ClaimConfidenceInfo,
  DocumentClaimSource,
  OCRCoordinates,
  TabularRowPreview,
} from '@billing/validation/intakeItemDocument';
import {
  Accordion,
  Alert,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Table,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { useTranslation } from '@refinedev/core';
import { useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import { useParsedOCRMetadata } from 'api/resource/intakeItem';
import { getDisplayNameFromStoredAttachmentName } from 'utils/intakeAttachmentKey';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface ClaimSourcePreviewProps {
  claimID: string;
  confidenceMetadata: ClaimConfidenceInfo;
}

const MINIMUM_PDF_WIDTH = 240;
const PDF_HORIZONTAL_PADDING = 24;
const MAXIMUM_ROW_HEIGHT = 320;
const HORIZONTAL_CROP_PADDING = 0.015;
const VERTICAL_CROP_PADDING = 0.01;
const DEFAULT_PAGE_ASPECT_RATIO = 8.5 / 11;

type CropRegion = Omit<OCRCoordinates, 'page'>;
type TabularClaimSource = Extract<
  DocumentClaimSource,
  { kind: 'csv' | 'workbook' }
>;

const buildCropRegion = (
  coordinates: OCRCoordinates[]
): CropRegion | undefined => {
  if (coordinates.length === 0) return undefined;

  const left = Math.max(
    Math.min(...coordinates.map((region) => region.left)) -
      HORIZONTAL_CROP_PADDING,
    0
  );
  const top = Math.max(
    Math.min(...coordinates.map((region) => region.top)) -
      VERTICAL_CROP_PADDING,
    0
  );
  const right = Math.min(
    Math.max(...coordinates.map((region) => region.left + region.width)) +
      HORIZONTAL_CROP_PADDING,
    1
  );
  const bottom = Math.min(
    Math.max(...coordinates.map((region) => region.top + region.height)) +
      VERTICAL_CROP_PADDING,
    1
  );

  return { left, top, width: right - left, height: bottom - top };
};

const UnavailablePreview = ({ regionMissing = false }) => {
  const { translate } = useTranslation();
  const message = regionMissing
    ? translate(
        'pages:claims.modals.ocrClaimRegionUnavailable',
        'The source document is available, but no saved region matches this claim.'
      )
    : translate(
        'pages:claims.modals.ocrSourcePreviewUnavailable',
        'A source preview is not available for this claim.'
      );

  return (
    <Alert color="yellow" variant="light">
      {message}
    </Alert>
  );
};

const ConfidenceDetails = ({
  confidenceMetadata,
  onClose,
}: {
  confidenceMetadata: ClaimConfidenceInfo;
  onClose: () => void;
}) => {
  const { translate } = useTranslation();
  const documentName = getDisplayNameFromStoredAttachmentName(
    confidenceMetadata.documentName
  );
  const sourceRowText =
    confidenceMetadata.page == null
      ? translate(
          'pages:claims.modals.ocrSourceRow',
          { row: confidenceMetadata.rowIndex },
          `Source row: ${confidenceMetadata.rowIndex}`
        )
      : translate(
          'pages:claims.modals.ocrSourceRowWithPage',
          {
            row: confidenceMetadata.rowIndex,
            page: confidenceMetadata.page,
          },
          `Source row: ${confidenceMetadata.rowIndex}, page ${confidenceMetadata.page}`
        );

  return (
    <Alert
      title={translate(
        'pages:claims.modals.ocrConfidenceTitle',
        'OCR confidence'
      )}
      color="blue"
      radius="sm"
      variant="light"
      mb="md"
      withCloseButton
      onClose={onClose}
    >
      {confidenceMetadata.confidence != null && (
        <Text>
          {translate(
            'pages:claims.modals.ocrConfidenceMessage',
            'This claim was created from OCR with a confidence score of '
          )}
          {confidenceMetadata.confidence.toFixed(2)}%
        </Text>
      )}
      <Text>
        {translate(
          'pages:claims.modals.ocrSourceDocument',
          { document: documentName },
          `Source document: ${documentName}`
        )}
      </Text>
      <Text>{sourceRowText}</Text>
    </Alert>
  );
};

const PdfSourcePreview = ({
  pdfURL,
  documentName,
  coordinates,
  fallbackPage,
}: {
  pdfURL: string;
  documentName: string;
  coordinates: OCRCoordinates[];
  fallbackPage: number | null | undefined;
}) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('dark');
  const { ref: previewRef, width: previewWidth } = useElementSize();
  const [pageAspectRatio, setPageAspectRatio] = useState(
    DEFAULT_PAGE_ASPECT_RATIO
  );
  const pdfFile = useMemo(() => ({ url: pdfURL }), [pdfURL]);
  const pageNumber = coordinates[0]?.page ?? fallbackPage ?? 1;
  const cropRegion = buildCropRegion(
    coordinates.filter((coordinate) => coordinate.page === pageNumber)
  );
  const availableCropWidth = Math.max(
    previewWidth - PDF_HORIZONTAL_PADDING,
    MINIMUM_PDF_WIDTH
  );
  const fullPageWidth = cropRegion
    ? Math.min(
        availableCropWidth / cropRegion.width,
        (MAXIMUM_ROW_HEIGHT * pageAspectRatio) / cropRegion.height
      )
    : availableCropWidth;
  const cropWidth = cropRegion
    ? fullPageWidth * cropRegion.width
    : availableCropWidth;
  const cropHeight = cropRegion
    ? (fullPageWidth / pageAspectRatio) * cropRegion.height
    : 0;

  return (
    <Flex direction="column" gap="sm">
      {!cropRegion && <UnavailablePreview regionMissing />}
      <Group justify="space-between" wrap="wrap">
        <Text size="sm" c="dimmed">
          {getDisplayNameFromStoredAttachmentName(documentName)}
        </Text>
        {cropRegion && (
          <Text size="sm">
            {translate(
              'pages:claims.modals.ocrSourcePage',
              { page: pageNumber },
              `Page ${pageNumber}`
            )}
          </Text>
        )}
      </Group>
      <Box ref={previewRef}>
        <Document
          file={pdfFile}
          loading={
            <Flex justify="center" p="xl">
              <Loader size="sm" />
            </Flex>
          }
          error={
            <Alert color="red">
              {translate(
                'pages:claims.modals.ocrSourcePreviewError',
                'Source preview could not be loaded'
              )}
            </Alert>
          }
        >
          {cropRegion && (
            <Box
              data-testid="claim-source-crop"
              pos="relative"
              w={cropWidth}
              h={cropHeight}
              mx="auto"
              style={{
                overflow: 'hidden',
                borderRadius: 8,
                background:
                  computedColorScheme === 'dark' ? '#1a1b1e' : '#e9ecef',
              }}
            >
              <Box
                pos="absolute"
                left={-cropRegion.left * fullPageWidth}
                top={-cropRegion.top * (fullPageWidth / pageAspectRatio)}
              >
                <Page
                  pageNumber={pageNumber}
                  width={fullPageWidth}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  onLoadSuccess={({ originalWidth, originalHeight }) =>
                    setPageAspectRatio(originalWidth / originalHeight)
                  }
                />
              </Box>
            </Box>
          )}
        </Document>
      </Box>
    </Flex>
  );
};

const TabularSourcePreview = ({
  documentName,
  source,
  row,
}: {
  documentName: string;
  source: TabularClaimSource;
  row: TabularRowPreview;
}) => {
  const { translate } = useTranslation();
  const sourceLocation =
    source.kind === 'csv'
      ? translate(
          'pages:claims.modals.ocrSourceRecord',
          { record: source.recordNumber },
          `Record ${source.recordNumber}`
        )
      : translate(
          'pages:claims.modals.ocrSourceWorkbookRow',
          { sheet: source.sheetName, row: source.rowNumber },
          `${source.sheetName}, row ${source.rowNumber}`
        );

  return (
    <Flex direction="column" gap="sm">
      <Group justify="space-between" wrap="wrap">
        <Text size="sm" c="dimmed">
          {getDisplayNameFromStoredAttachmentName(documentName)}
        </Text>
        <Text size="sm">{sourceLocation}</Text>
      </Group>
      <Table.ScrollContainer minWidth={Math.max(row.headers.length * 160, 480)}>
        <Table
          striped
          withTableBorder
          withColumnBorders
          data-testid="claim-source-tabular-row"
        >
          <Table.Thead>
            <Table.Tr>
              {row.headers.map((header, index) => (
                <Table.Th key={`${header}-${index}`}>
                  {header ||
                    translate(
                      'pages:claims.modals.ocrSourceColumn',
                      { column: index + 1 },
                      `Column ${index + 1}`
                    )}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              {row.values.map((value, index) => (
                <Table.Td key={index}>{value}</Table.Td>
              ))}
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Flex>
  );
};

export const ClaimSourcePreview = ({
  claimID,
  confidenceMetadata,
}: ClaimSourcePreviewProps) => {
  const { translate } = useTranslation();
  const [showConfidenceDetails, setShowConfidenceDetails] = useState(true);
  const { data, isError, isLoading, refetch } = useParsedOCRMetadata(
    confidenceMetadata.documentID
  );
  const claimEvidence = data?.claims.find((claim) => claim.claimID === claimID);
  const tabularSource = claimEvidence?.source;
  const tabularRow = claimEvidence?.tabularRow;
  const hasTabularPreview = Boolean(tabularSource && tabularRow);
  const previewReady = !isLoading && !isError;
  const previewUnavailable =
    previewReady && !data?.pdfURL && !hasTabularPreview;

  return (
    <Card mt="lg">
      <Accordion
        defaultValue="ocr-source-preview"
        variant="contained"
        data-testid="claim-source-preview"
      >
        <Accordion.Item value="ocr-source-preview">
          <Accordion.Control>
            <Text fw={600}>
              {translate(
                'pages:claims.modals.ocrSourcePreviewTitle',
                'OCR details'
              )}
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            {showConfidenceDetails && (
              <ConfidenceDetails
                confidenceMetadata={confidenceMetadata}
                onClose={() => setShowConfidenceDetails(false)}
              />
            )}

            {isLoading && (
              <Flex align="center" justify="center" mih={240} gap="sm">
                <Loader size="sm" />
                <Text size="sm">
                  {translate(
                    'pages:claims.modals.ocrSourcePreviewLoading',
                    'Loading source preview…'
                  )}
                </Text>
              </Flex>
            )}

            {isError && (
              <Alert
                color="red"
                title={translate(
                  'pages:claims.modals.ocrSourcePreviewError',
                  'Source preview could not be loaded'
                )}
              >
                <Button size="xs" variant="light" onClick={() => refetch()}>
                  {translate('buttons.retry', 'Retry')}
                </Button>
              </Alert>
            )}

            {previewUnavailable && <UnavailablePreview />}

            {previewReady && data?.pdfURL && (
              <PdfSourcePreview
                pdfURL={data.pdfURL}
                documentName={data.documentName}
                coordinates={claimEvidence?.coordinates ?? []}
                fallbackPage={claimEvidence?.page}
              />
            )}

            {previewReady && data && tabularSource && tabularRow && (
              <TabularSourcePreview
                documentName={data.documentName}
                source={tabularSource}
                row={tabularRow}
              />
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Card>
  );
};
